import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Mail, ShieldCheck, KeyRound, ArrowLeft, Copy, Smartphone, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, finishMfa, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA state
  const [step, setStep] = useState("password"); // password | change_password | setup | verify
  const [mfaToken, setMfaToken] = useState("");
  const [pwdToken, setPwdToken] = useState("");
  const [qrPng, setQrPng] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwdConfirm, setNewPwdConfirm] = useState("");

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.password_change_required) {
        setPwdToken(data.pwd_token);
        setStep("change_password");
        toast.info("Defina sua nova senha pessoal para continuar.");
      } else if (data?.mfa_required) {
        setMfaToken(data.mfa_token);
        if (data.mfa_setup_required) {
          setStep("setup");
          setQrPng(data.qr_png_b64 || "");
          setSecret(data.secret || "");
          toast.info("Configure seu app autenticador para continuar.");
        } else {
          setStep("verify");
          toast.info("Digite o código do seu app autenticador.");
        }
      } else {
        toast.success("Bem-vindo(a)!");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("A senha deve ter ao menos 8 caracteres.");
      return;
    }
    if (newPwd !== newPwdConfirm) {
      toast.error("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { pwd_token: pwdToken, new_password: newPwd });
      toast.success("Senha atualizada. Entre novamente para configurar o MFA.");
      // Re-login with new password to continue the flow (MFA setup)
      const data = await login(email, newPwd);
      if (data?.mfa_required && data.mfa_setup_required) {
        setMfaToken(data.mfa_token);
        setStep("setup");
        setQrPng(data.qr_png_b64 || "");
        setSecret(data.secret || "");
      } else if (data?.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep("verify");
      } else {
        navigate("/admin");
      }
      setPassword("");
      setNewPwd("");
      setNewPwdConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e, endpoint) => {
    e.preventDefault();
    if (!code || code.length < 6) {
      toast.error("Digite o código de 6 dígitos.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/auth/${endpoint}`, {
        mfa_token: mfaToken,
        code: code.trim(),
      });
      finishMfa(data);
      toast.success(endpoint === "mfa/confirm" ? "MFA ativado com sucesso!" : "Bem-vindo(a)!");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep("password");
    setMfaToken("");
    setPwdToken("");
    setQrPng("");
    setSecret("");
    setCode("");
    setPassword("");
    setNewPwd("");
    setNewPwdConfirm("");
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret).then(
      () => toast.success("Chave copiada."),
      () => toast.error("Não foi possível copiar."),
    );
  };

  return (
    <div className="min-h-screen bg-[#050608] text-white flex flex-col">
      <header className="border-b border-[#1C1F26]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="logo-home-btn">
            <Logo />
          </button>
          <div className="text-xs text-[#6B8DB3] uppercase tracking-[0.28em]">
            Acesso administrativo
          </div>
        </div>
      </header>

      <main className="flex-1 grid lg:grid-cols-2">
        {/* Left side */}
        <div className="hidden lg:flex relative items-center justify-center p-12 border-r border-[#1C1F26] overflow-hidden">
          <div className="absolute inset-0 bg-grid-faint opacity-50" aria-hidden="true" />
          <div
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-25"
            style={{ background: "radial-gradient(circle, #013A63 0%, transparent 60%)" }}
          />
          <div
            className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-25"
            style={{ background: "radial-gradient(circle, #B9262B 0%, transparent 60%)" }}
          />
          <div className="relative max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1C1F26] border border-[#3A3F47] text-xs uppercase tracking-[0.28em] text-[#6B8DB3] mb-6">
              <ShieldCheck size={14} className="text-[#22B673]" />
              Área restrita · Acesso com MFA
            </div>
            <h2 className="text-4xl font-bold tracking-tight leading-tight">
              Painel UniGR de Diagnóstico
            </h2>
            <p className="mt-4 text-[#9aa3ad] leading-relaxed">
              Gerencie sessões, perguntas e visualize todos os diagnósticos
              respondidos pelos clientes. Login protegido com autenticação de dois fatores.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md fade-slide">
            {step === "password" && (
              <form onSubmit={submitPassword} data-testid="login-form">
                <h1 className="text-2xl font-semibold mb-1">Entrar no Painel</h1>
                <p className="text-sm text-[#6B8DB3] mb-8">
                  Use suas credenciais de administrador.
                </p>

                <div className="space-y-5">
                  <div>
                    <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
                      E-mail
                    </Label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]" />
                      <Input
                        data-testid="login-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="suporte@imgf-unigr.com.br"
                        className="bg-[#1C1F26] border-[#3A3F47] text-white h-12 pl-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
                      Senha
                    </Label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]" />
                      <Input
                        data-testid="login-password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="bg-[#1C1F26] border-[#3A3F47] text-white h-12 pl-9"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full mt-7 bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold h-12 glow-accent"
                >
                  {loading ? <Loader2 className="animate-spin" /> : "Continuar"}
                </Button>
              </form>
            )}

            {step === "change_password" && (
              <form onSubmit={submitChangePassword} data-testid="change-password-form">
                <button
                  type="button"
                  onClick={resetFlow}
                  className="flex items-center gap-1 text-xs text-[#6B8DB3] hover:text-white mb-4"
                >
                  <ArrowLeft size={12} /> Voltar
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5A623]/10 border border-[#F5A623]/40 text-[10px] uppercase tracking-[0.28em] text-[#F5A623] mb-4">
                  <AlertCircle size={12} /> Senha temporária detectada
                </div>
                <h1 className="text-2xl font-semibold mb-2">Defina uma nova senha</h1>
                <p className="text-sm text-[#9aa3ad] mb-6 leading-relaxed">
                  Para sua segurança, substitua a senha temporária por uma nova senha pessoal antes de continuar.
                </p>

                <div className="space-y-4">
                  <div>
                    <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
                      Nova senha (mínimo 8 caracteres)
                    </Label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]" />
                      <Input
                        data-testid="new-password-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        placeholder="••••••••"
                        autoFocus
                        className="bg-[#1C1F26] border-[#3A3F47] text-white h-12 pl-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
                      Confirme a nova senha
                    </Label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]" />
                      <Input
                        data-testid="new-password-confirm-input"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={newPwdConfirm}
                        onChange={(e) => setNewPwdConfirm(e.target.value)}
                        placeholder="••••••••"
                        className="bg-[#1C1F26] border-[#3A3F47] text-white h-12 pl-9"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || newPwd.length < 8 || newPwd !== newPwdConfirm}
                  data-testid="change-password-submit-btn"
                  className="w-full mt-7 bg-[#22B673] hover:bg-[#1a9b60] text-white font-semibold h-12"
                >
                  {loading ? <Loader2 className="animate-spin" /> : "Salvar e continuar"}
                </Button>
              </form>
            )}

            {step === "setup" && (
              <form onSubmit={(e) => submitCode(e, "mfa/confirm")} data-testid="mfa-setup-form">
                <button
                  type="button"
                  onClick={resetFlow}
                  className="flex items-center gap-1 text-xs text-[#6B8DB3] hover:text-white mb-4"
                >
                  <ArrowLeft size={12} /> Voltar
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#22B673]/10 border border-[#22B673]/40 text-[10px] uppercase tracking-[0.28em] text-[#22B673] mb-4">
                  <Smartphone size={12} /> Configurar autenticador
                </div>
                <h1 className="text-2xl font-semibold mb-2">Ative o MFA</h1>
                <p className="text-sm text-[#9aa3ad] mb-6 leading-relaxed">
                  Escaneie o QR code abaixo com o <strong>Google Authenticator</strong>,
                  Authy ou 1Password e digite o código gerado para concluir o cadastro.
                </p>

                {qrPng && (
                  <div className="flex flex-col items-center mb-5">
                    <div className="p-3 bg-white rounded-lg" data-testid="mfa-qr">
                      <img
                        src={`data:image/png;base64,${qrPng}`}
                        alt="QR code MFA"
                        className="w-48 h-48"
                      />
                    </div>
                  </div>
                )}

                {secret && (
                  <div className="mb-5 p-3 rounded-lg border border-[#3A3F47] bg-[#1C1F26]">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B8DB3] mb-1">
                      Chave manual (caso não consiga escanear)
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <code
                        data-testid="mfa-secret"
                        className="text-xs font-mono text-[#E6E6E8] break-all"
                      >
                        {secret}
                      </code>
                      <button
                        type="button"
                        onClick={copySecret}
                        className="text-[#6B8DB3] hover:text-white"
                        title="Copiar"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                )}

                <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
                  Código de 6 dígitos
                </Label>
                <div className="relative mb-5">
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]" />
                  <Input
                    data-testid="mfa-code-input"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    autoFocus
                    className="bg-[#1C1F26] border-[#3A3F47] text-white h-12 pl-9 font-mono tracking-[0.5em] text-center text-lg"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || code.length < 6}
                  data-testid="mfa-confirm-btn"
                  className="w-full bg-[#22B673] hover:bg-[#1a9b60] text-white font-semibold h-12"
                >
                  {loading ? <Loader2 className="animate-spin" /> : "Ativar MFA e entrar"}
                </Button>
              </form>
            )}

            {step === "verify" && (
              <form onSubmit={(e) => submitCode(e, "mfa/verify")} data-testid="mfa-verify-form">
                <button
                  type="button"
                  onClick={resetFlow}
                  className="flex items-center gap-1 text-xs text-[#6B8DB3] hover:text-white mb-4"
                >
                  <ArrowLeft size={12} /> Voltar
                </button>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A2342]/30 border border-[#6B8DB3]/40 text-[10px] uppercase tracking-[0.28em] text-[#6B8DB3] mb-4">
                  <ShieldCheck size={12} /> Verificação de 2 fatores
                </div>
                <h1 className="text-2xl font-semibold mb-2">Código do autenticador</h1>
                <p className="text-sm text-[#9aa3ad] mb-6">
                  Digite o código de 6 dígitos exibido no seu app autenticador.
                </p>

                <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider mb-2 block">
                  Código
                </Label>
                <div className="relative mb-5">
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B8DB3]" />
                  <Input
                    data-testid="mfa-code-input"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    autoFocus
                    className="bg-[#1C1F26] border-[#3A3F47] text-white h-12 pl-9 font-mono tracking-[0.5em] text-center text-lg"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || code.length < 6}
                  data-testid="mfa-verify-btn"
                  className="w-full bg-[#B9262B] hover:bg-[#9a1e22] text-white font-semibold h-12 glow-accent"
                >
                  {loading ? <Loader2 className="animate-spin" /> : "Entrar"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLogin;
